import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useWallet } from '../../chain/WalletContext';
import { walletSignIn } from '../../chain/walletAuth';
import { shortAddress } from '../../chain/config';
import { BpMark } from '../BpLogo';

const CATEGORIES = [
    ['electronics', 'Electronics'], ['fashion', 'Fashion'], ['grocery', 'Grocery'],
    ['home & living', 'Home & Living'], ['beauty & health', 'Beauty & Health'],
    ['sports & outdoors', 'Sports & Outdoors'], ['books & media', 'Books & Media'],
    ['services', 'Services'], ['delivery service', 'Delivery Service'], ['other', 'Other'],
];

// /join/business — wallet sign-in, then the business profile, then the dashboard.
export default function BusinessSignup() {
    const { address, connect, hasWallet } = useWallet();
    const [step, setStep] = useState(1);           // 1 = wallet, 2 = business details
    const [phase, setPhase] = useState('idle');
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        businessName: '', businessType: 'other', phone: '',
        address: '', city: '', state: '', webUrl: '', about: '',
    });
    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    const connectAndSign = async () => {
        setError('');
        try {
            let addr = address;
            if (!addr) {
                setPhase('connecting');
                addr = await connect();
                if (!addr) { setPhase('idle'); return; }
            }
            setPhase('signing');
            await walletSignIn(addr);
            setPhase('idle');
            setStep(2);
        } catch (err) {
            setPhase('idle');
            setError(err.message === 'NO_WALLET'
                ? 'No wallet detected — install BirrWallet or MetaMask to continue.'
                : err.message);
        }
    };

    const submitBusiness = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.businessName || !form.phone || !form.address || !form.city) {
            setError('Business name, phone, address, and city are required.');
            return;
        }
        if (form.phone.replace(/\D/g, '').length < 9) {
            setError('Enter a valid phone number.');
            return;
        }
        setPhase('saving');
        try {
            const res = await axios.post(`${process.env.REACT_APP_APP_API_URL}/api/editprofile`, {
                businessName: form.businessName,
                businessType: form.businessType,
                phone: form.phone,
                address: form.address,
                city: form.city,
                state: form.state,
                webUrl: form.webUrl,
                about: form.about,
                walletAddress: address, // BRC payouts land straight in the signing wallet
            }, { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });
            if (res.data.status !== 200) throw new Error(res.data.message || 'Could not save your business');
            toast.success('Your shop is live — welcome to BirrPlug!');
            window.location.replace('/dashboard');
        } catch (err) {
            setPhase('idle');
            setError(err.message);
        }
    };

    return (
        <div className="bp-auth-page">
            <div className="bp-auth-card bp-auth-card-wide">
                <div className="bp-auth-mark"><BpMark size={52} /></div>
                <h1>Open your shop</h1>
                <p className="bp-auth-sub">
                    Sell on BirrPlug and get paid in BRC <strong>directly to your wallet</strong> —
                    start free with 10 product listings.
                </p>

                <div className="bp-biz-steps">
                    <span className={step >= 1 ? 'done' : ''}>1 · Wallet</span>
                    <i />
                    <span className={step >= 2 ? 'done' : ''}>2 · Business details</span>
                    <i />
                    <span>3 · Dashboard</span>
                </div>

                {step === 1 && (
                    <>
                        {address && (
                            <div className="bp-auth-wallet-pill">
                                <span className="bp-wallet-dot live" />
                                <span className="bp-mono">{shortAddress(address)}</span>
                                <span className="bp-dim">connected</span>
                            </div>
                        )}
                        <button className="bp-btn-primary bp-auth-cta" onClick={connectAndSign} disabled={phase !== 'idle'}>
                            {phase === 'connecting' ? 'Connecting wallet…'
                                : phase === 'signing' ? 'Check your wallet — sign the message'
                                : address ? 'Sign & Continue' : 'Connect Wallet to Start'}
                        </button>
                        <p className="bp-dim bp-auth-hint">This wallet becomes your shop's payout address. You can change it later in the dashboard.</p>
                        {!hasWallet && (
                            <a className="bp-auth-getwallet" href="https://birr.foundation/wallet" target="_blank" rel="noreferrer">
                                Don't have a wallet? Get BirrWallet ↗
                            </a>
                        )}
                    </>
                )}

                {step === 2 && (
                    <form className="bp-biz-form" onSubmit={submitBusiness}>
                        <div className="bp-field-row">
                            <div className="bp-field">
                                <label>Business name *</label>
                                <input className="bp-auth-input" placeholder="e.g. TechHub Addis" value={form.businessName} onChange={set('businessName')} />
                            </div>
                            <div className="bp-field">
                                <label>Category *</label>
                                <select className="bp-auth-input" value={form.businessType} onChange={set('businessType')}>
                                    {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="bp-field-row">
                            <div className="bp-field">
                                <label>Phone *</label>
                                <input className="bp-auth-input" placeholder="+251 …" value={form.phone} onChange={set('phone')} />
                            </div>
                            <div className="bp-field">
                                <label>Website <span className="bp-dim">(optional)</span></label>
                                <input className="bp-auth-input" placeholder="https://…" value={form.webUrl} onChange={set('webUrl')} />
                            </div>
                        </div>
                        <div className="bp-field">
                            <label>Street address *</label>
                            <input className="bp-auth-input" placeholder="Where customers find you" value={form.address} onChange={set('address')} />
                        </div>
                        <div className="bp-field-row">
                            <div className="bp-field">
                                <label>City *</label>
                                <input className="bp-auth-input" placeholder="e.g. Addis Ababa" value={form.city} onChange={set('city')} />
                            </div>
                            <div className="bp-field">
                                <label>Region / State <span className="bp-dim">(optional)</span></label>
                                <input className="bp-auth-input" value={form.state} onChange={set('state')} />
                            </div>
                        </div>
                        <div className="bp-field">
                            <label>About your shop <span className="bp-dim">(optional)</span></label>
                            <input className="bp-auth-input" placeholder="One line customers will see on your page" value={form.about} onChange={set('about')} />
                        </div>
                        <button type="submit" className="bp-btn-primary bp-auth-cta" disabled={phase === 'saving'}>
                            {phase === 'saving' ? 'Creating your shop…' : 'Launch My Shop →'}
                        </button>
                    </form>
                )}

                {error && <p className="bp-auth-error">{error}</p>}

                <div className="bp-auth-links">
                    <span>Just shopping? <Link to="/signup">Create a buyer account</Link></span>
                    <span>Already selling? <Link to="/login">Sign in</Link></span>
                </div>
            </div>
        </div>
    );
}
